import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAllSclasses, getSubjectDetails, updateSclass } from "../../../redux/sclassRelated/sclassHandle";
import {
    fetchTeacherTemporaryEmail,
    getAllTeachers,
    removeTeachSubject,
    updateTeachSubject,
} from "../../../redux/teacherRelated/teacherHandle";
import {
    HOMEROOM_ASSIGN_SUBJECTS_MSG,
    syncTeacherSubjectsForClass,
} from "../../../utils/homeroomAssignSubjects";
import { isBursar, isHomeroomTeacherRole, isLibrarian } from "../../../utils/staffRoles";
import { authSuccess } from "../../../redux/userRelated/userSlice";
import Popup from "../../../components/Popup";
import { registerUser } from "../../../redux/userRelated/userHandle";
import { underControl } from "../../../redux/userRelated/userSlice";
import { formatAdminError, ERROR_HINTS } from "../../../utils/feedbackMessages";
import {
    getStaffRoleLabel,
    normalizeStaffRole,
    staffRoleNeedsSubjectFlow,
} from "../../../utils/staffRoles";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { PASSWORD_MIN_HELPER, validateNewPassword } from "../../../utils/passwordValidation";
import {
    alpha,
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";

const AddStaff = () => {
    const params = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const subjectID = params.id;
    const staffRole = normalizeStaffRole(
        location.state?.staffRole || (subjectID ? "Teacher" : location.state?.role)
    );
    const homeroomClassId = location.state?.homeroomClassId || "";
    const homeroomClassName = location.state?.homeroomClassName || "";
    const homeroomSubjectIds = Array.isArray(location.state?.homeroomSubjectIds)
        ? location.state.homeroomSubjectIds
        : [];
    const isHomeroomSetup =
        isHomeroomTeacherRole(staffRole) && Boolean(homeroomClassId) && homeroomSubjectIds.length > 0;

    const { status, response, error } = useSelector((state) => state.user);
    const { currentUser } = useSelector((state) => state.user);
    const { subjectDetails } = useSelector((state) => state.sclass);

    useEffect(() => {
        if (subjectID) {
            dispatch(getSubjectDetails(subjectID, "Subject"));
        }
    }, [dispatch, subjectID]);

    const [temporaryEmail, setTemporaryEmail] = useState("");
    const [emailLoading, setEmailLoading] = useState(true);
    const [password, setPassword] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [loader, setLoader] = useState(false);
    const [credentials, setCredentials] = useState(null);
    const [createdStaff, setCreatedStaff] = useState(null);

    const school =
        getSchoolIdForUser(currentUser) ||
        (subjectDetails &&
            (subjectDetails.schoolId || subjectDetails.school?._id || subjectDetails.school));
    const teachSubject = subjectDetails && (subjectDetails._id || subjectDetails.id);
    const teachSclass =
        subjectDetails &&
        subjectDetails.sclassName &&
        (subjectDetails.sclassName._id || subjectDetails.sclassName.id);

    const roleLabel = getStaffRoleLabel(staffRole, currentUser?.schoolType);
    const needsSubjects = staffRoleNeedsSubjectFlow(staffRole);

    const loadTemporaryEmail = useCallback(async () => {
        setEmailLoading(true);
        try {
            const email = await fetchTeacherTemporaryEmail();
            if (email) setTemporaryEmail(email);
        } catch {
            setMessage("Could not generate a temporary email. Try again.");
            setShowPopup(true);
        } finally {
            setEmailLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTemporaryEmail();
    }, [loadTemporaryEmail]);

    useEffect(() => {
        if (!isHomeroomTeacherRole(staffRole) || subjectID) return;
        if (!isHomeroomSetup) {
            navigate("/Admin/teachers/chooseclass", { state: { staffRole }, replace: true });
        }
    }, [isHomeroomSetup, navigate, staffRole, subjectID]);

    const copyText = async (text, successMsg) => {
        try {
            await navigator.clipboard.writeText(text);
            setMessage(successMsg);
            setShowPopup(true);
        } catch {
            setMessage("Could not copy. Select and copy manually.");
            setShowPopup(true);
        }
    };

    const submitHandler = async (event) => {
        event.preventDefault();
        if (!school) {
            setMessage("School data is still loading. Wait a moment and try again.");
            setShowPopup(true);
            return;
        }
        if (!temporaryEmail) {
            setMessage("Wait for the temporary login email to be generated.");
            setShowPopup(true);
            return;
        }
        if (isHomeroomTeacherRole(staffRole) && !subjectID && !isHomeroomSetup) {
            setMessage(HOMEROOM_ASSIGN_SUBJECTS_MSG);
            setShowPopup(true);
            return;
        }
        const passwordErr = validateNewPassword(password);
        if (passwordErr) {
            setMessage(passwordErr);
            setShowPopup(true);
            return;
        }
        setLoader(true);
        const created = await dispatch(
            registerUser(
                {
                    email: temporaryEmail,
                    password,
                    role: staffRole,
                    school,
                    teachSubject: needsSubjects ? teachSubject : undefined,
                    teachSclass: needsSubjects ? teachSclass : undefined,
                },
                "Teacher"
            )
        );
        setLoader(false);
        if (created?.email) {
            const teacherId = created._id || created.id;
            if (isHomeroomSetup && teacherId) {
                setLoader(true);
                const syncOutcome = await syncTeacherSubjectsForClass(dispatch, {
                    teacherId,
                    classId: homeroomClassId,
                    selectedSubjectIds: homeroomSubjectIds,
                    teacher: created,
                    updateTeachSubject,
                    removeTeachSubject,
                });
                if (!syncOutcome.success) {
                    setLoader(false);
                    setMessage(
                        syncOutcome.message ||
                            "Account created but subjects could not be assigned. Finish setup from All classes or Staff.",
                    );
                    setShowPopup(true);
                    return;
                }
                const classOutcome = await dispatch(
                    updateSclass(homeroomClassId, { classTeacherId: teacherId }),
                );
                setLoader(false);
                if (classOutcome?.success === false) {
                    setMessage(
                        classOutcome.message ||
                            "Account and subjects saved, but homeroom class link failed. Assign them on the class page.",
                    );
                    setShowPopup(true);
                }
                if (school) dispatch(getAllSclasses(school, "Sclass"));
            }
            dispatch(underControl());
            if (school) dispatch(getAllTeachers(school));
            if (isBursar(staffRole) && currentUser) {
                dispatch(authSuccess({ ...currentUser, hasBursar: true }));
            }
            if (isLibrarian(staffRole) && currentUser) {
                dispatch(authSuccess({ ...currentUser, hasLibrarian: true }));
            }
            setCredentials({ email: created.email, password });
            setCreatedStaff(created);
        }
    };

    useEffect(() => {
        if (status === "failed") {
            setMessage(formatAdminError(response, "Could not add staff. Check all fields and try again."));
            setShowPopup(true);
            setLoader(false);
        } else if (status === "error") {
            setMessage(formatAdminError(error, ERROR_HINTS.network));
            setShowPopup(true);
            setLoader(false);
        }
    }, [status, error, response]);

    const copyCredentials = async () => {
        if (!credentials) return;
        const text = `First-time ${roleLabel.toLowerCase()} login\n\nTemporary email: ${credentials.email}\nPassword: ${credentials.password}\n\nSign in with these credentials, then complete your profile and set your personal email.`;
        await copyText(text, "Login details copied to clipboard.");
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 40%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3.5 },
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <PersonAddAlt1Icon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Invite {roleLabel.toLowerCase()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Share the temporary email and password below. They sign in once, then complete their
                                profile.
                            </Typography>
                        </Box>
                    </Stack>

                    {subjectDetails && needsSubjects && (
                        <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Role:</strong> {roleLabel}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Subject:</strong> {subjectDetails.subName}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Class:</strong>{" "}
                                {subjectDetails.sclassName && subjectDetails.sclassName.sclassName}
                            </Typography>
                        </Stack>
                    )}

                    {isHomeroomSetup ? (
                        <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Role:</strong> {roleLabel}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Class:</strong> {homeroomClassName}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Subjects:</strong> {homeroomSubjectIds.length} selected
                            </Typography>
                            <Button
                                size="small"
                                sx={{ alignSelf: "flex-start" }}
                                onClick={() =>
                                    navigate(`/Admin/staff/homeroom/${homeroomClassId}`, {
                                        state: {
                                            staffRole,
                                            className: homeroomClassName,
                                            homeroomSubjectIds,
                                        },
                                    })
                                }
                            >
                                Change class or subjects
                            </Button>
                        </Stack>
                    ) : null}

                    {!subjectDetails && !needsSubjects && !isHomeroomSetup && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Role:</strong> {roleLabel}
                            {staffRole === "Bursar" ? (
                                <>
                                    {" "}
                                    — they handle day-to-day fees and expenses in their portal. You keep full visibility
                                    in your finance menus.
                                </>
                            ) : null}
                            {staffRole === "Librarian" ? (
                                <>
                                    {" "}
                                    — they manage books, TLMs, and borrowing. Assign subjects under Staff if they also
                                    teach. You review library analytics under Advanced analytics.
                                </>
                            ) : null}
                        </Typography>
                    )}

                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        Give both credentials before first sign-in. After login they must complete their profile and
                        replace the temporary email.
                        {needsSubjects ? (
                            <>
                                {" "}
                                You can assign <strong>additional subjects</strong> later from the staff list.
                            </>
                        ) : null}
                    </Alert>

                    <Divider sx={{ my: 2 }} />

                    <Box component="form" onSubmit={submitHandler}>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Temporary login email"
                                value={temporaryEmail}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Copy email">
                                                <span>
                                                    <IconButton
                                                        onClick={() =>
                                                            copyText(temporaryEmail, "Temporary email copied.")
                                                        }
                                                        disabled={!temporaryEmail || emailLoading}
                                                        edge="end"
                                                        aria-label="Copy temporary email"
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Generate new email">
                                                <span>
                                                    <IconButton
                                                        onClick={loadTemporaryEmail}
                                                        disabled={emailLoading || loader}
                                                        edge="end"
                                                        aria-label="Regenerate temporary email"
                                                    >
                                                        {emailLoading ? (
                                                            <CircularProgress size={20} />
                                                        ) : (
                                                            <RefreshIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                                helperText="Used for first login until they set a personal email."
                            />
                            <TextField
                                fullWidth
                                required
                                type="password"
                                label="Temporary password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                helperText={`${PASSWORD_MIN_HELPER}. Share securely; they can change it after completing their profile.`}
                                inputProps={{ minLength: 8 }}
                            />
                        </Stack>

                        <Stack direction="row" spacing={2} sx={{ mt: 4 }} justifyContent="flex-end">
                            <Button variant="outlined" onClick={() => navigate("/Admin/teachers")} disabled={loader}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loader || emailLoading || !temporaryEmail}
                            >
                                {loader ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    `Create ${roleLabel.toLowerCase()} account`
                                )}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Container>

            <Dialog open={Boolean(credentials)} onClose={() => navigate("/Admin/teachers")} maxWidth="sm" fullWidth>
                <DialogTitle>{roleLabel} account created</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {isHomeroomSetup
                            ? `${roleLabel} is set for ${homeroomClassName} with ${homeroomSubjectIds.length} subject(s). Share these first-time login credentials.`
                            : `Share these first-time login credentials with the ${roleLabel.toLowerCase()}.`}
                    </Alert>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Temporary login email
                            </Typography>
                            <Typography variant="body1" fontWeight={700} sx={{ wordBreak: "break-all" }}>
                                {credentials?.email}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Temporary password
                            </Typography>
                            <Typography variant="body1" fontWeight={700}>
                                {credentials?.password}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
                    <Button startIcon={<ContentCopyIcon />} onClick={copyCredentials}>
                        Copy login details
                    </Button>
                    {needsSubjects && (createdStaff?._id || createdStaff?.id) ? (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                const tid = createdStaff._id || createdStaff.id;
                                navigate(`/Admin/teachers/chooseclass/${tid}`);
                            }}
                        >
                            Assign more subjects
                        </Button>
                    ) : null}
                    <Button variant="contained" onClick={() => navigate("/Admin/teachers")}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default AddStaff;
